<template>
  <div class="top-notice">
    <div class="top-notice__trigger" @click="openDrawer">
      <el-badge :value="unreadCount" :hidden="unreadCount < 1" :max="99">
        <i class="el-icon-bell"></i>
      </el-badge>
    </div>

    <el-drawer
      :visible.sync="drawerVisible"
      direction="rtl"
      size="920px"
      append-to-body
      :wrapper-closable="true"
      :with-header="false"
      custom-class="desk-notification-drawer"
      @open="onDrawerOpen"
    >
      <div class="desk-notification">
        <header class="desk-notification__header-bar">
          <div class="desk-notification__header-left">
            <i class="el-icon-bell desk-notification__header-bell"></i>
            <span class="desk-notification__header-text">消息通知中心</span>
          </div>
          <div class="desk-notification__header-right">
            <i
              class="el-icon-search desk-notification__header-tool"
              title="搜索"
            ></i>
            <span class="desk-notification__user-ring" title="用户">
              <i class="el-icon-user-solid"></i>
            </span>
            <button
              type="button"
              class="desk-notification__close-btn"
              aria-label="关闭"
              @click="drawerVisible = false"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>
        </header>

        <div class="desk-notification__toolbar">
          <div class="desk-notification__tabs">
            <button
              v-for="tab in tabList"
              :key="tab.value"
              type="button"
              class="desk-notification__tab"
              :class="{ 'is-active': activeMsgType === tab.value }"
              @click="setMsgType(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
          <div class="desk-notification__bulk">
            <button
              type="button"
              class="desk-notification__link"
              @click="handleReadAll"
            >
              <span class="desk-notification__dbl-check" aria-hidden="true">
                <i class="el-icon-check"></i><i class="el-icon-check"></i>
              </span>
              全部已读
            </button>
            <button
              type="button"
              class="desk-notification__link"
              :disabled="!selectedIds.length"
              @click="handleReadBatch"
            >
              <i class="el-icon-message"></i>
              批量已读
            </button>
            <button
              type="button"
              class="desk-notification__link desk-notification__link--danger"
              :disabled="!selectedIds.length"
              @click="handleRemoveBatch"
            >
              <i class="el-icon-delete"></i>
              批量删除
            </button>
          </div>
        </div>

        <div
          class="desk-notification__list-wrap"
          v-loading="loading"
        >
          <el-scrollbar class="desk-notification__scrollbar">
            <div v-if="!list.length && !loading" class="desk-notification__empty">
              暂无消息
            </div>
            <el-checkbox-group
              v-else
              v-model="selectedIds"
              class="desk-notification__checkbox-group"
            >
            <div
              v-for="item in list"
              :key="item.id"
              class="desk-notification__card"
              :class="{ 'is-unread': !item.isRead }"
            >
              <div class="desk-notification__card-inner">
                <el-checkbox
                  class="desk-notification__check"
                  :label="item.id"
                />
                <div
                  class="desk-notification__icon"
                  :style="{ background: iconMeta(item).bg }"
                >
                  <i :class="iconMeta(item).icon"></i>
                </div>
                <div class="desk-notification__main">
                  <div class="desk-notification__title-row">
                    <div class="desk-notification__title-cluster">
                      <span
                        v-if="!item.isRead"
                        class="desk-notification__unread-dot"
                        aria-hidden="true"
                      ></span>
                      <span class="desk-notification__title">{{ item.title }}</span>
                    </div>
                    <span
                      v-if="item.categoryLabel"
                      class="desk-notification__tag"
                      :class="tagClass(item)"
                    >{{ item.categoryLabel }}</span>
                  </div>
                  <p class="desk-notification__content">{{ item.content }}</p>
                  <div class="desk-notification__time-sub">
                    <i class="el-icon-time desk-notification__time-ico"></i>
                    <span>{{ item.time }}</span>
                  </div>
                </div>
                <div class="desk-notification__aside">
                  <div class="desk-notification__time-top">{{ item.time }}</div>
                  <div class="desk-notification__row-actions">
                    <el-tooltip content="标记已读" placement="top">
                      <span
                        class="desk-notification__icon-btn"
                        :class="{ 'is-disabled': item.isRead }"
                        @click="!item.isRead && markOneRead(item)"
                      >
                        <i class="el-icon-success"></i>
                      </span>
                    </el-tooltip>
                    <el-tooltip content="删除" placement="top">
                      <span
                        class="desk-notification__icon-btn"
                        @click="removeOne(item)"
                      >
                        <i class="el-icon-delete"></i>
                      </span>
                    </el-tooltip>
                  </div>
                </div>
              </div>
            </div>
            </el-checkbox-group>
          </el-scrollbar>
        </div>

        <div class="desk-notification__pager" v-if="total > size">
          <el-pagination
            small
            background
            layout="prev, pager, next, total"
            :current-page="current"
            :page-size="size"
            :total="total"
            @current-change="onPageChange"
          />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  getNotificationPage,
  getUnreadCount,
  readOne,
  readBatch,
  readAll,
  removeBatch,
} from '@/api/desk/notification';

const TYPE_ICON = {
  alert: { icon: 'el-icon-warning', bg: '#e53935' },
  task: { icon: 'el-icon-document', bg: '#43a047' },
  system: { icon: 'el-icon-info', bg: '#1e88e5' },
  inspect: { icon: 'el-icon-view', bg: '#1e88e5' },
};

/** 未读数（角标）轮询间隔：5 分钟 */
const UNREAD_POLL_MS = 5 * 60 * 1000;

export default {
  name: 'top-notice',
  data() {
    return {
      drawerVisible: false,
      unreadPollTimer: null,
      activeMsgType: 'all',
      tabList: [
        { label: '全部消息', value: 'all' },
        { label: '预警报警', value: 'alert' },
        { label: '工单任务', value: 'task' },
        { label: '系统通知', value: 'system' },
        { label: '巡检隐患', value: 'inspect' },
      ],
      list: [],
      total: 0,
      current: 1,
      size: 10,
      loading: false,
      selectedIds: [],
      unreadCount: 0,
    };
  },
  created() {
    this.refreshUnread();
    this.startUnreadPoll();
  },
  beforeDestroy() {
    this.stopUnreadPoll();
  },
  methods: {
    openDrawer() {
      this.drawerVisible = true;
    },
    startUnreadPoll() {
      this.stopUnreadPoll();
      this.unreadPollTimer = setInterval(() => {
        this.refreshUnread();
        if (this.drawerVisible) {
          this.fetchList();
        }
      }, UNREAD_POLL_MS);
    },
    stopUnreadPoll() {
      if (this.unreadPollTimer) {
        clearInterval(this.unreadPollTimer);
        this.unreadPollTimer = null;
      }
    },
    onDrawerOpen() {
      this.current = 1;
      this.selectedIds = [];
      this.fetchList();
      this.refreshUnread();
    },
    iconMeta(item) {
      const t = (item.type || '').toLowerCase();
      return TYPE_ICON[t] || TYPE_ICON.system;
    },
    tagClass(item) {
      const t = (item.type || '').toLowerCase();
      const map = {
        alert: 'desk-notification__tag--alert',
        task: 'desk-notification__tag--task',
        system: 'desk-notification__tag--system',
        inspect: 'desk-notification__tag--inspect',
      };
      return map[t] || 'desk-notification__tag--system';
    },
    setMsgType(val) {
      if (this.activeMsgType === val) return;
      this.activeMsgType = val;
      this.current = 1;
      this.selectedIds = [];
      this.fetchList();
    },
    onPageChange(page) {
      this.current = page;
      this.selectedIds = [];
      this.fetchList();
    },
    fetchList() {
      this.loading = true;
      getNotificationPage({
        msgType: this.activeMsgType,
        current: this.current,
        size: this.size,
      })
        .then((res) => {
          const data = res.data.data || {};
          this.list = data.records || [];
          this.total = data.total != null ? data.total : 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refreshUnread() {
      getUnreadCount({ msgType: 'all' }).then((res) => {
        const d = res.data.data || {};
        this.unreadCount = typeof d.count === 'number' ? d.count : 0;
      });
    },
    markOneRead(item) {
      readOne(item.id).then(() => {
        item.isRead = true;
        this.refreshUnread();
      });
    },
    handleReadAll() {
      readAll(this.activeMsgType).then(() => {
        this.$message.success('操作成功');
        this.fetchList();
        this.refreshUnread();
      });
    },
    handleReadBatch() {
      if (!this.selectedIds.length) return;
      readBatch(this.selectedIds.join(',')).then(() => {
        this.$message.success('已标记已读');
        this.selectedIds = [];
        this.fetchList();
        this.refreshUnread();
      });
    },
    handleRemoveBatch() {
      if (!this.selectedIds.length) return;
      this.$confirm('确定删除选中的消息？', '提示', {
        type: 'warning',
      }).then(() => {
        removeBatch(this.selectedIds.join(',')).then(() => {
          this.$message.success('已删除');
          this.selectedIds = [];
          this.fetchList();
          this.refreshUnread();
        });
      });
    },
    removeOne(item) {
      this.$confirm('确定删除该消息？', '提示', { type: 'warning' }).then(() => {
        removeBatch(String(item.id)).then(() => {
          this.$message.success('已删除');
          this.fetchList();
          this.refreshUnread();
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.top-notice__trigger {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  line-height: 1;
}
</style>

<style lang="scss">
/* drawer 挂载到 body，非 scoped */
.desk-notification-drawer {
  .el-drawer__body {
    padding: 0;
    background: #f5f7fa;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.desk-notification {
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  font-size: 14px;
  color: #333;

  &__header-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    background: #fff;
    border-bottom: 1px solid #ebeef5;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__header-bell {
    font-size: 22px;
    color: #409eff;
  }

  &__header-text {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  &__header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__header-tool {
    font-size: 18px;
    color: #909399;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #409eff;
    }
  }

  &__user-ring {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid #dcdfe6;
    background: #f5f7fa;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    cursor: default;
    font-size: 16px;
  }

  &__close-btn {
    border: none;
    background: transparent;
    padding: 4px;
    margin: 0 0 0 4px;
    cursor: pointer;
    color: #909399;
    font-size: 18px;
    line-height: 1;
    border-radius: 4px;
    transition: color 0.2s, background 0.2s;

    &:hover {
      color: #409eff;
      background: #ecf5ff;
    }
  }

  &__toolbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 20px 10px;
    background: #f5f7fa;
  }

  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__tab {
    border: none;
    border-radius: 18px;
    padding: 7px 16px;
    font-size: 13px;
    cursor: pointer;
    background: #eef0f3;
    color: #606266;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: #e4e7ed;
    }

    &.is-active {
      background: #ecf5ff;
      color: #409eff;
      font-weight: 500;
    }
  }

  &__bulk {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  &__dbl-check {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    margin-right: 2px;

    .el-icon-check {
      font-size: 11px;
      font-weight: bold;
    }
  }

  &__link {
    border: none;
    background: transparent;
    padding: 0;
    font-size: 13px;
    color: #606266;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;

    &:hover:not(:disabled) {
      color: #409eff;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    &--danger:hover:not(:disabled) {
      color: #f56c6c;
    }

    i {
      font-size: 15px;
    }
  }

  &__list-wrap {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 0 20px;
    background: #f5f7fa;
    overflow: hidden;
  }

  &__scrollbar {
    flex: 1;
    min-height: 200px;

    .el-scrollbar__wrap {
      overflow-x: hidden;
    }
  }

  &__empty {
    text-align: center;
    color: #909399;
    padding: 48px 16px;
    font-size: 14px;
  }

  &__card {
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    margin-bottom: 12px;
    overflow: hidden;
    transition: box-shadow 0.2s, border-color 0.2s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
      border-color: #dcdfe6;
    }

    &.is-unread {
      border-left: 3px solid #ff9900;
    }

    &:not(.is-unread) {
      border-left: 3px solid transparent;
    }
  }

  &__card-inner {
    display: flex;
    align-items: flex-start;
    padding: 14px 16px 14px 12px;
    gap: 12px;
  }

  &__checkbox-group {
    width: 100%;
  }

  &__check {
    margin-top: 6px;
    flex-shrink: 0;

    .el-checkbox__label {
      display: none;
      padding-left: 0;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    line-height: 1;

    i {
      color: #fff;
    }
  }

  &__unread-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ff9900;
    flex-shrink: 0;
    margin-top: 6px;
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__title-row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px 10px;
    margin-bottom: 8px;
  }

  &__title-cluster {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-weight: 600;
    color: #303133;
    font-size: 14px;
    line-height: 1.45;
    flex: 1;
    min-width: 0;
  }

  &__tag {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 4px;
    line-height: 1.4;
    white-space: nowrap;

    &--alert {
      color: #e53935;
      background: #ffebee;
    }

    &--task {
      color: #2e7d32;
      background: #e8f5e9;
    }

    &--system,
    &--inspect {
      color: #1565c0;
      background: #e3f2fd;
    }
  }

  &__content {
    margin: 0 0 8px;
    font-size: 13px;
    color: #666;
    line-height: 1.55;
    word-break: break-word;
  }

  &__time-sub {
    font-size: 12px;
    color: #999;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__time-ico {
    font-size: 13px;
  }

  &__aside {
    flex-shrink: 0;
    text-align: right;
    min-width: 132px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    min-height: 76px;
  }

  &__time-top {
    font-size: 12px;
    color: #999;
  }

  &__row-actions {
    display: flex;
    gap: 14px;
    margin-top: auto;
  }

  &__icon-btn {
    cursor: pointer;
    color: #909399;
    font-size: 17px;
    line-height: 1;
    transition: color 0.2s;

    &:hover {
      color: #409eff;
    }

    &.is-disabled {
      opacity: 0.35;
      cursor: default;
      pointer-events: none;
    }
  }

  &__pager {
    flex-shrink: 0;
    padding: 8px 20px 16px;
    display: flex;
    justify-content: flex-end;
    background: #f5f7fa;
    border-top: 1px solid transparent;
  }
}
</style>
